import { VisualisationContextProps } from 'widgets/supportVisualisation/Content/VisualisationContentProps';
import React, { useState } from 'react';
import { Menu } from '@instinet-analytics/semantic-ui-components';

import { StreamedDataKey } from 'streamsState/store/streamedData/StreamedDataState';
import { WidgetPanelGrid } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelGrid';
import { showNiceDate } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanel';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import './SupportVisualisationBloombergMessages.scss';
import '../../SupportVisualisationMenu.scss';
import { requestUid } from 'marketData/MarketDataRequest';
import { SupportVisualisationBloombergMessage } from 'widgets/supportVisualisation/Content/BloombergMessages/SupportVisualisationBloombergMessage';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';
import omit from 'lodash/omit';
import { BloombergRequest } from 'app/glue/bloomberg/store/bloombergSlice';
import orderBy from 'lodash/orderBy';

type FlattenedData = BloombergRequest & { key: string };

export default function SupportVisualisationBloombergMessages(
    props: VisualisationContextProps
) {
    const { state, width, height } = props;

    const style = { width, height };

    const [currentRequestUid, setCurrentRequestUid] = useState<
        string | undefined
    >();
    const [currentResponseKey, setCurrentResponseKey] = useState<
        string | undefined
    >();

    const bloombergState = state?.bloombergMessages;
    if (!bloombergState?.requests) {
        return <div>None In State</div>;
    }

    const requestData = orderBy(
        flattenData(bloombergState.requests),
        ['requestNumber'],
        ['desc']
    );

    const toUniqueUid = (d: FlattenedData) =>
        `${d.requestNumber}-${d.key}`
            .replace('mktbarBar', 'RealTimeBar')
            .replace('IntraDayBarRequestBar', 'HistBar')
            .replace('ReferenceDataRequestBar', 'RefData');
    const requestUIds = requestData.map(toUniqueUid);
    const selectedRequest = requestData.find(
        (d) => toUniqueUid(d) === currentRequestUid
    );
    const selectedRequestResponses = selectedRequest
        ? [...selectedRequest.messages, ...selectedRequest.statuses]
        : [];

    if (selectedRequestResponses) {
        for (const d of selectedRequestResponses) {
            showNiceDate(d, 'timestamp');
        }
    }

    const selectedResponse = selectedRequestResponses.find(
        (r) => r.messageNumber.toString() === currentResponseKey
    );

    const handleSelectedRowChanged = (ids: string[]) => {
        setCurrentResponseKey(ids.length === 1 ? ids[0] : undefined);
    };

    const showResponsePanel =
        selectedResponse && selectedResponse.eventType !== 'RequestStatus';

    return (
        <div
            className="SupportVisualisationBloombergMessages streamed-data"
            style={style}
        >
            <div>
                <Menu
                    pointing
                    vertical
                    className="SupportVisualisationMenu streamedDataKeys"
                >
                    {requestUIds.map((u) => (
                        <KeyLink
                            key={u}
                            streamedDataKey={u as StreamedDataKey}
                        />
                    ))}
                </Menu>
            </div>

            <div className="bars-panel">
                <WidgetPanelGrid
                    data={selectedRequestResponses}
                    getRowDataId={(d) => d.messageNumber.toString()}
                    csvFileName={'market-requestData'}
                    colDefs={getResponseColDefs()}
                    onSelectedRowIdsChanged={handleSelectedRowChanged}
                />
                {selectedRequest && (
                    <JsonPanel
                        className="request-details"
                        title="Request"
                        data={omit(selectedRequest, 'messages')}
                    />
                )}
                {showResponsePanel && (
                    <div className="current-row">
                        <SupportVisualisationBloombergMessage
                            message={selectedResponse}
                        />
                    </div>
                )}
            </div>
        </div>
    );

    function onChangeCurrentKey(nextKey: StreamedDataKey) {
        setCurrentRequestUid(nextKey);
        setCurrentResponseKey(undefined);
    }

    function KeyLink(props: { streamedDataKey: StreamedDataKey }) {
        const { streamedDataKey } = props;
        return (
            <Menu.Item
                active={streamedDataKey === currentRequestUid}
                onClick={() => onChangeCurrentKey(streamedDataKey)}
            >
                {streamedDataKey}
            </Menu.Item>
        );
    }
}

function flattenData(requests: BloombergRequest[]): FlattenedData[] {
    return requests.map((b) => ({
        key: `${b.requestType}${requestUid(b.request)}`,
        ...b,
    }));
}

function getResponseColDefs(): ColDef[] {
    const timeDisplay: ColDef = {
        field: 'timestampDisplay',
        width: 130,
        resizable: true,
        headerName: 'Time',
    };
    const cols = ['eventType', 'timestamp'].map((key) => ({
        field: key,
        width: 120,
        resizable: true,
    }));
    const response: ColDef = {
        field: 'response',
        width: 130,
        resizable: true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        cellRenderer: (props: { value?: any; data?: { status?: string } }) => {
            const { data, value } = props;

            if (typeof value === 'string') {
                return <span>{value}</span>;
            }
            if (value?.data?.barTickData?.length !== undefined) {
                return (
                    <span>{value?.data?.barTickData?.length + ' bars'}</span>
                );
            }
            if (value?.msg) {
                return (
                    <span>
                        {value?.msg?.eventMessages?.length + ' event messages'}
                    </span>
                );
            }
            if (data?.status) {
                return <span>{data?.status}</span>;
            }
            return <span>{value !== undefined ? 'Yes' : 'None'}</span>;
        },
    };

    return [timeDisplay, ...cols, response];
}
